import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import DownloadBanner from '../components/DownloadBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFlag, 
    faShield, 
    faLock, 
    faBrain, 
    faNetworkWired, 
    faChartLine, 
    faUsers, 
    faHeadset, 
    faEnvelope, 
    faCopy, 
    faDownload, 
    faStore, 
    faClipboardCheck, 
    faCodeBranch, 
    faCircleCheck 
} from '@fortawesome/free-solid-svg-icons';
import './AboutUs.css';

const AboutUs = () => {
    const { t } = useTranslation();

    const handleCopyEmail = () => {
        navigator.clipboard.writeText('team.thebetteryou@gmail.com');
        alert('Email copied to clipboard!');
    };

    return (
        <main className="w-100 pt-5 mt-4">
            <div className="d-flex flex-column w-100">

                {/* Atmospheric Header Section */}
                <section className="position-relative overflow-hidden bg-gradient bg-body-tertiary pt-5 pb-5" style={{ background: 'radial-gradient(circle at top right, rgba(98,0,169,0.03), transparent 40%), linear-gradient(to bottom, #ffffff, #FAF5FF20, #f8f9ff)' }}>
                    {/* Subtle Purple Ambient Glow Blob (Opacity lowered to 3% for soft atmospheric feel) */}
                    <div className="position-absolute top-0 end-0 translate-middle-y rounded-circle pointer-events-none" style={{ width: '380px', height: '380px', filter: 'blur(80px)', background: 'var(--brand-purple, #6200a9)', opacity: 0.03 }}></div>
                    
                    <div className="container-xl px-4 py-4">
                        <div className="d-flex flex-column align-items-center text-center mx-auto" style={{ maxWidth: '720px' }}>
                            <div className="inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-accent-lavender shadow-sm mb-3">
                                <span className="rounded-circle bg-brand-purple animate-pulse" style={{ width: '8px', height: '8px', display: 'inline-block' }}></span>
                                <span className="label-md-text text-brand-purple tracking-wider text-uppercase">OUR PURPOSE &amp; PHILOSOPHY</span>
                            </div>
                            {/* Headline: "About" in dark text, "Us" in solid brand purple */}
                            <h1 className="display-hero-text text-text-primary mb-3">
                                About <span className="text-brand-purple">Us</span>
                            </h1>
                            {/* Subtitle */}
                            <p className="body-xl-text text-text-secondary mb-4">
                                Learn more about our mission to help people improve their mental well-being and personal growth through actionable, scientifically backed insights.
                            </p>
                            {/* Quick Stat Pills */}
                            <div className="row g-3 w-100 justify-content-center pt-2">
                                <div className="col-6 col-sm-4">
                                    <div className="p-3 rounded-4 bg-white shadow-sm text-center">
                                        <span className="d-block headline-lg-text text-brand-purple fw-bold">5</span>
                                        <span className="d-block text-secondary" style={{ fontSize: '12px' }}>Core Schema Profiles</span>
                                    </div>
                                </div>
                                <div className="col-6 col-sm-4">
                                    <div className="p-3 rounded-4 bg-white shadow-sm text-center">
                                        <span className="d-block headline-lg-text text-brand-purple fw-bold">CBT</span>
                                        <span className="d-block text-secondary" style={{ fontSize: '12px' }}>Evidence-Based Root</span>
                                    </div>
                                </div>
                                <div className="col-12 col-sm-4">
                                    <div className="p-3 rounded-4 bg-white shadow-sm text-center">
                                        <span className="d-block headline-lg-text text-brand-purple fw-bold">100%</span>
                                        <span className="d-block text-secondary" style={{ fontSize: '12px' }}>Privacy-First Architecture</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="container-xl px-4 py-5">
                    <div className="row g-4">

                        {/* Card 1: Our Mission */}
                        <div className="col-lg-7">
                            <div className="bento-card h-100 d-flex flex-column justify-content-between p-4 p-lg-5">
                                <div className="position-absolute top-0 end-0 bg-accent-lavender rounded-bottom-left-pill opacity-60 pointer-events-none" style={{ width: '190px', height: '190px', borderBottomLeftRadius: '100%' }}></div>
                                <div className="position-relative z-1 mb-4">
                                    <div className="rounded-3 bg-accent-lavender text-brand-purple d-flex align-items-center justify-content-center shadow-sm mb-3" style={{ width: '48px', height: '48px' }}>
                                        <FontAwesomeIcon icon={faFlag} size="lg" />
                                    </div>
                                    <span className="label-md-text text-brand-purple text-uppercase tracking-wider">Vision</span>
                                    <h2 className="headline-xl-text text-text-primary mt-1 mb-3">Our Mission</h2>
                                    <p className="body-lg-text text-text-secondary">
                                        At TheBetterYou, our mission is to empower individuals to achieve better mental well-being and personal growth through evidence-based psychological tools and personalized guidance. We believe that everyone deserves access to resources that can help them understand themselves better and make positive changes in their lives.
                                    </p>
                                </div>
                                <div className="position-relative z-1 d-flex flex-wrap align-items-center gap-3 label-md-text text-secondary pt-3 border-top">
                                    <span className="d-inline-flex align-items-center gap-1 text-brand-purple fw-bold"><FontAwesomeIcon icon={faCircleCheck} className="text-brand-purple" /> Accessible</span>
                                    <span className="d-inline-flex align-items-center gap-1 text-brand-purple fw-bold"><FontAwesomeIcon icon={faCircleCheck} className="text-brand-purple" /> Compassionate</span>
                                    <span className="d-inline-flex align-items-center gap-1 text-brand-purple fw-bold"><FontAwesomeIcon icon={faCircleCheck} className="text-brand-purple" /> Transformative</span>
                                </div>
                            </div>
                        </div>

                        {/* Card 2: Visual Metaphor Feature */}
                        <div className="col-lg-5">
                            <div className="bento-card h-100 p-3 d-flex flex-column justify-content-between">
                                <div className="position-relative rounded-4 overflow-hidden h-100 min-vh-50" style={{ minHeight: '240px' }}>
                                    <img className="w-100 h-100 object-fit-cover" src="meditating-woman.jpg" alt="Meditation room serenity" />
                                    <div className="position-absolute inset-0 bg-gradient-to-t" style={{ background: 'linear-gradient(to top, rgba(15,23,42,0.8), transparent)' }}></div>
                                    <div className="position-absolute bottom-0 start-0 end-0 p-4 text-white">
                                        <span className="label-md-text tracking-wider uppercase text-light opacity-75">THEBETTERYOU PLATFORM</span>
                                        <p className="headline-sm-text text-white fw-bold mt-1 mb-0">Self-awareness is the catalyst for genuine behavioral change.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card 3: What We Do */}
                        <div className="col-lg-6">
                            <div className="bento-card h-100 d-flex flex-column justify-content-between p-4 p-lg-5">
                                <div>
                                    <div className="rounded-3 bg-accent-lavender text-brand-purple d-flex align-items-center justify-content-center shadow-sm mb-3" style={{ width: '48px', height: '48px' }}>
                                        <FontAwesomeIcon icon={faBrain} size="lg" />
                                    </div>
                                    <span className="label-md-text text-brand-purple text-uppercase tracking-wider">Methodology</span>
                                    <h2 className="headline-xl-text text-text-primary mt-1 mb-3">What We Do</h2>
                                    <p className="body-lg-text text-text-secondary mb-4">
                                        TheBetterYou is a mobile application that uses Schema Therapy principles to help users understand their psychological patterns. Through introductory assessments, users discover their psychological profile and receive personalized tasks, articles, and tools tailored to their specific needs and goals.
                                    </p>
                                </div>
                                {/* Mini Workflow */}
                                <div className="row g-2 pt-3">
                                    <div className="col-4">
                                        <div className="p-3 rounded-3 bg-canvas-slate text-center">
                                            <FontAwesomeIcon icon={faClipboardCheck} className="text-brand-purple mb-1" size="lg" />
                                            <span className="d-block label-md-text text-text-primary fw-bold">1. Assess</span>
                                            <span className="d-block text-muted" style={{ fontSize: '11px' }}>Target patterns</span>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="p-3 rounded-3 bg-canvas-slate text-center">
                                            <FontAwesomeIcon icon={faNetworkWired} className="text-brand-purple mb-1" size="lg" />
                                            <span className="d-block label-md-text text-text-primary fw-bold">2. Map</span>
                                            <span className="d-block text-muted" style={{ fontSize: '11px' }}>Psych profile</span>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="p-3 rounded-3 bg-canvas-slate text-center">
                                            <FontAwesomeIcon icon={faChartLine} className="text-brand-purple mb-1" size="lg" />
                                            <span className="d-block label-md-text text-text-primary fw-bold">3. Evolve</span>
                                            <span className="d-block text-muted" style={{ fontSize: '11px' }}>Custom tools</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card 4: Our Approach */}
                        <div className="col-lg-6">
                            <div className="bento-card h-100 d-flex flex-column justify-content-between p-4 p-lg-5">
                                <div>
                                    <div className="rounded-3 bg-accent-lavender text-brand-purple d-flex align-items-center justify-content-center shadow-sm mb-3" style={{ width: '48px', height: '48px' }}>
                                        <FontAwesomeIcon icon={faCodeBranch} size="lg" />
                                    </div>
                                    <span className="label-md-text text-brand-purple text-uppercase tracking-wider">Foundation</span>
                                    <h2 className="headline-xl-text text-text-primary mt-1 mb-3">Our Approach</h2>
                                    <p className="body-lg-text text-text-secondary mb-4">
                                        We base our approach on Schema Therapy, a well-established therapeutic model that helps individuals understand and change long-standing patterns of thinking, feeling, and behaving. Our app translates these therapeutic principles into accessible, user-friendly tools that support personal growth and mental well-being.
                                    </p>
                                </div>
                                {/* Therapy Blueprint Tags */}
                                <div className="d-flex flex-wrap gap-2 pt-2">
                                    <span className="px-3 py-1 rounded-pill bg-accent-lavender label-md-text text-brand-purple">Cognitive Reframing</span>
                                    <span className="px-3 py-1 rounded-pill bg-accent-lavender label-md-text text-brand-purple">Experiential Techniques</span>
                                    <span className="px-3 py-1 rounded-pill bg-accent-lavender label-md-text text-brand-purple">Behavioral Pattern Breaking</span>
                                </div>
                            </div>
                        </div>

                        {/* Card 5: Our Team */}
                        <div className="col-12">
                            <div className="bento-card p-4 p-lg-5">
                                <div className="row g-4 align-items-center">
                                    <div className="col-lg-5">
                                        <div className="rounded-3 bg-accent-lavender text-brand-purple d-flex align-items-center justify-content-center shadow-sm mb-3" style={{ width: '48px', height: '48px' }}>
                                            <FontAwesomeIcon icon={faUsers} size="lg" />
                                        </div>
                                        <span className="label-md-text text-brand-purple text-uppercase tracking-wider">The Minds Behind</span>
                                        <h2 className="headline-xl-text text-text-primary mt-1 mb-3">Our Team</h2>
                                        <p className="body-lg-text text-text-secondary mb-3">
                                            TheBetterYou is brought to you by a dedicated team of experts combining clinical psychology and modern software engineering. Our mission is to bridge the gap between complex therapeutic principles and user-friendly digital tools.
                                        </p>
                                        <div className="rounded-3 bg-accent-lavender p-3">
                                            <p className="text-secondary fst-italic mb-0" style={{ fontSize: '13px' }}>
                                                "Clinical accuracy meets intuitive product experience, turning therapeutic frameworks into daily life transformation."
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-lg-7">
                                        <div className="row g-3">

                                            <div className="col-md-6">
                                                <div className="rounded-3 bg-canvas-slate p-4 h-100 d-flex flex-column justify-content-between">
                                                    <div>
                                                        <div className="rounded-2 bg-white text-brand-purple d-flex align-items-center justify-content-center shadow-sm mb-3" style={{ width: '40px', height: '40px' }}>
                                                            <FontAwesomeIcon icon={faShield} />
                                                        </div>
                                                        <h3 className="headline-sm-text text-text-primary mb-2">Clinical Expertise</h3>
                                                        <p className="text-secondary mb-3" style={{ fontSize: '14px' }}>TheBetterYou is an analytical self-assessment and CBT/Schema exploration platform informed by established psychological principles, designed for self-guided discovery, habit reflection, and personal growth.</p>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-1 text-success label-md-text">
                                                        <FontAwesomeIcon icon={faCircleCheck} /> Evidence-Validated
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div className="rounded-3 bg-canvas-slate p-4 h-100 d-flex flex-column justify-content-between">
                                                    <div>
                                                        <div className="rounded-2 bg-white text-brand-purple d-flex align-items-center justify-content-center shadow-sm mb-3" style={{ width: '40px', height: '40px' }}>
                                                            <FontAwesomeIcon icon={faLock} />
                                                        </div>
                                                        <h3 className="headline-sm-text text-text-primary mb-2">Technical Excellence</h3>
                                                        <p className="text-secondary mb-3" style={{ fontSize: '14px' }}>Our development team focuses on building a secure, scalable, and intuitive platform designed to protect your privacy while supporting your growth.</p>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-1 text-brand-purple label-md-text">
                                                        <FontAwesomeIcon icon={faLock} /> End-to-End Encrypted
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card 6: Contact Us (Action Banner) */}
                        <div className="col-12">
                            <div className="bento-card p-4 p-lg-5 bg-gradient" style={{ background: 'linear-gradient(135deg, var(--accent-lavender), #ffffff, var(--surface-bg))' }}>
                                <div className="row align-items-center g-4">
                                    <div className="col-lg-7">
                                        <div className="d-flex align-items-center gap-2 text-brand-purple label-md-text text-uppercase tracking-wider mb-2">
                                            <FontAwesomeIcon icon={faHeadset} />
                                            <span>We're Here For You</span>
                                        </div>
                                        <h2 className="headline-xl-text text-text-primary mb-2">Contact Us</h2>
                                        <p className="body-lg-text text-text-secondary mb-0">
                                            We'd love to hear from you! If you have any questions, feedback, or need support, please don't hesitate to reach out to our team.
                                        </p>
                                    </div>
                                    <div className="col-lg-5 d-flex flex-sm-row flex-column align-items-stretch justify-content-lg-end gap-2">
                                        <a href="mailto:team.thebetteryou@gmail.com" className="btn bg-white shadow-sm text-brand-purple label-md-text px-4 py-3 rounded-3 text-decoration-none d-flex align-items-center justify-content-center gap-2">
                                            <FontAwesomeIcon icon={faEnvelope} />
                                            <span>team.thebetteryou@gmail.com</span>
                                        </a>
                                        <button className="btn btn-primary-custom px-3 py-3 rounded-3 shadow-sm d-flex align-items-center justify-content-center" onClick={handleCopyEmail} title="Copy email">
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </section>

                {/* Bottom CTA: Start Your Journey Today */}
                <DownloadBanner />

            </div>
        </main>
    );
};

export default AboutUs;